/**
 * useAsconYears — 경원(아스콘) 여러 해의 수주·계근(출하실적 화면용, Firestore 미러 한 번 읽기).
 *
 * useAsconMonthly 와 같은 방식: 결과에 연도 묶음 키를 붙여 두고 지금 묶음과 맞을 때만 보여 준다
 * (연도·탭을 바꾸면 옛 값이 바로 사라짐). 지나간 기간을 보는 화면이라 구독·폴링하지 않는다.
 */
import { useEffect, useState } from "react";
import type { AsconOrder, AsconTicket } from "@/domain/asconShipment";
import { asconShipmentRepo, type AsconSource } from "@/services/asconShipment.repository";

interface Result {
  key: string;
  orders: AsconOrder[];
  tickets: AsconTicket[];
  source: AsconSource | null;
  error: string | null;
}

const EMPTY_O: AsconOrder[] = [];
const EMPTY_T: AsconTicket[] = [];

export interface UseAsconYearsState {
  orders: AsconOrder[];
  tickets: AsconTicket[];
  source: AsconSource | null;
  loading: boolean;
  error: string | null;
}

export function useAsconYears(years: number[]): UseAsconYearsState {
  const key = [...years].sort().join(",");
  const [res, setRes] = useState<Result | null>(null);
  useEffect(() => {
    if (!key) return;
    let alive = true;
    Promise.all(key.split(",").map((y) => asconShipmentRepo.fetchYear(Number(y)))).then(
      (parts) => {
        if (!alive) return;
        setRes({
          key,
          orders: parts.flatMap((p) => p.orders),
          tickets: parts.flatMap((p) => p.tickets),
          source: parts[0]?.source ?? null,
          error: null,
        });
      },
      (e: unknown) => {
        if (!alive) return;
        const msg = e instanceof Error ? e.message : String(e);
        setRes({
          key, orders: EMPTY_O, tickets: EMPTY_T, source: null,
          error: msg.includes("permission") ? "경원 출하 자료를 볼 권한이 없습니다" : msg,
        });
      },
    );
    return () => { alive = false; };
  }, [key]);
  const cur = key && res && res.key === key ? res : null;
  return {
    orders: cur?.orders ?? EMPTY_O,
    tickets: cur?.tickets ?? EMPTY_T,
    source: cur?.source ?? null,
    loading: Boolean(key) && !cur,
    error: cur?.error ?? null,
  };
}
